import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Headset, X, Loader2, Send, RefreshCw } from "lucide-react";
import LeadCaptureModal from "./LeadCaptureModal";
import { submitAgentLead } from "../services/marketingApi";

/*
 * Floating chat assistant (bottom-right), driven by the Verasist embed widget.
 * The embed token is configured as `embedMode: "headless"`, so the widget
 * renders no UI of its own — this component owns the button + chat panel and
 * talks to `window.VerasistChatWidget` (start / end / sendMessage / onMessage /
 * onStatusChange).
 */

const MAX_ATTEMPTS = 50; // widget load poll (300ms apart → ~15s budget)

const ChatWidgetButton = () => {
  const [status, setStatus] = useState("idle"); // idle | connecting | connected | failed
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [tools, setTools] = useState([]);
  const [busy, setBusy] = useState(false);
  const [chatError, setChatError] = useState("");
  const [panelOpen, setPanelOpen] = useState(false);
  const sendingRef = useRef(false);
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadError, setLeadError] = useState("");
  const readyRef = useRef(false);
  const subscribedRef = useRef(false);
  const attemptsRef = useRef(0);
  const listRef = useRef(null);


  // Neon accent per status (matches the "Talk to agent" neon button look).
  const neonColor =
    status === "connected"
      ? "#e74c3c"
      : status === "connecting"
        ? "#f59e0b"
        : status === "failed"
          ? "#ef4444"
          : "var(--brand-cyan)";

  // ── Wait for widget to initialize, then subscribe once ──
  useEffect(() => {
    let timer = null;

    const waitForWidget = () => {
      attemptsRef.current += 1;

      if (attemptsRef.current > MAX_ATTEMPTS) {
        setStatus("failed");
        console.error("[ChatWidgetButton] Verasist widget did not initialize");
        return;
      }

      const w = window.VerasistChatWidget;
      if (!w) {
        timer = setTimeout(waitForWidget, 300);
        return;
      }

      try {
        const st = w.getState ? w.getState() : null;
        if (!st || !st.isInitialized) {
          timer = setTimeout(waitForWidget, 300);
          return;
        }
      } catch (_) {
        timer = setTimeout(waitForWidget, 300);
        return;
      }

      readyRef.current = true;

      if (!subscribedRef.current) {
        subscribedRef.current = true;
        w.onStatusChange((newStatus) => { setStatus(newStatus); if (["connecting", "connected"].includes(newStatus)) setPanelOpen(true); });
        w.onMessage((newMessages, progress) => {
          setMessages((newMessages || []).map((message) => ({ ...message })));
          setBusy(progress?.busy ?? Boolean(w.getState?.()?.chatBusy));
          setChatError(progress?.error || "");
          setTools(progress?.tools || []);
        });
        w.onCallEnd(() => { setStatus("idle"); setBusy(false); });
        w.onError(() => setStatus("failed"));
      }
    };

    waitForWidget();

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  // ── Keep chat scrolled to the newest message ──
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const handleToggle = useCallback(() => {
    const w = window.VerasistChatWidget;
    if (!w || !readyRef.current) return;

    if (panelOpen) {
      setPanelOpen(false);
      w.end();
    } else {
      setLeadError("");
      setLeadModalOpen(true);
    }
  }, [panelOpen]);

  const handleLeadSubmit = useCallback(async (leadValues) => {
    setLeadSubmitting(true);
    setLeadError("");
    try {
      await submitAgentLead({ ...leadValues, source: "chatbot" });
      setLeadModalOpen(false);
      const w = window.VerasistChatWidget;
      if (w && readyRef.current) {
        setMessages([]);
        setStatus("connecting");
        w.start();
      }
    } catch (err) {
      setLeadError(err?.message || "Bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setLeadSubmitting(false);
    }
  }, []);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    const widget = window.VerasistChatWidget;
    if (!text || status !== "connected" || busy || sendingRef.current || widget?.getState?.()?.chatBusy) return;
    sendingRef.current = true;
    setBusy(true); setInput(""); setChatError("");
    try { await widget?.sendMessage(text); }
    catch (error) { setChatError(error.message || "Yanıt tamamlanamadı."); }
    finally { sendingRef.current = false; setBusy(Boolean(widget?.getState?.()?.chatBusy)); }
  }, [input, status, busy]);

  return (
    <>
      <style>{`
        .chat-widget-fab {
          background: transparent;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid var(--chat-neon);
          color: var(--chat-neon);
          box-shadow:
            0 0 12px color-mix(in srgb, var(--chat-neon) 40%, transparent),
            inset 0 0 10px color-mix(in srgb, var(--chat-neon) 12%, transparent);
          transition: background 0.2s ease;
          animation: chat-fab-blink 2.4s ease-in-out infinite;
        }
        .chat-widget-fab:hover {
          animation: none;
          background: color-mix(in srgb, var(--chat-neon) 10%, transparent);
          box-shadow:
            0 0 24px color-mix(in srgb, var(--chat-neon) 70%, transparent),
            inset 0 0 14px color-mix(in srgb, var(--chat-neon) 22%, transparent);
        }
        @keyframes chat-fab-blink {
          0%, 100% {
            box-shadow:
              0 0 6px color-mix(in srgb, var(--chat-neon) 28%, transparent),
              inset 0 0 8px color-mix(in srgb, var(--chat-neon) 8%, transparent);
          }
          50% {
            box-shadow:
              0 0 24px color-mix(in srgb, var(--chat-neon) 75%, transparent),
              inset 0 0 14px color-mix(in srgb, var(--chat-neon) 22%, transparent);
          }
        }
        .chat-widget-input::placeholder {
          color: var(--muted);
        }
        .chat-widget-send {
          background: transparent;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid var(--brand-cyan);
          color: var(--brand-cyan);
          box-shadow:
            0 0 12px color-mix(in srgb, var(--brand-cyan) 40%, transparent),
            inset 0 0 10px color-mix(in srgb, var(--brand-cyan) 12%, transparent);
          transition: box-shadow 0.2s ease, background 0.2s ease, opacity 0.2s ease;
        }
        .chat-widget-send:hover:not(:disabled) {
          background: color-mix(in srgb, var(--brand-cyan) 10%, transparent);
          box-shadow:
            0 0 22px color-mix(in srgb, var(--brand-cyan) 65%, transparent),
            inset 0 0 14px color-mix(in srgb, var(--brand-cyan) 22%, transparent);
        }
        .chat-widget-send:disabled {
          opacity: 0.45;
          box-shadow: none;
        }
      `}</style>

      {/* ── Floating toggle button (bottom-right, next to ScrollNav) ── */}
      <motion.button
        type="button"
        onClick={handleToggle}
        aria-label={panelOpen ? "Sohbeti kapat" : "Sohbeti başlat"}
        whileHover={{ scale: 1.07 }}
        whileTap={{ scale: 0.94 }}
        className="chat-widget-fab fixed bottom-6 z-50 flex items-center justify-center w-14 h-14 rounded-full select-none left-1/2 -ml-7 md:left-auto md:right-[4.25rem] md:ml-0"
        style={{ "--chat-neon": neonColor }}
      >
        {status === "connected" ? (
          <X size={22} />
        ) : status === "connecting" ? (
          <Loader2 size={22} className="animate-spin" />
        ) : status === "failed" ? (
          <RefreshCw size={20} />
        ) : (
          <Headset size={24} />
        )}
      </motion.button>

      {/* ── Chat panel (visible while live) ── */}
      <AnimatePresence>
        {panelOpen && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed bottom-24 z-50 flex flex-col overflow-hidden rounded-2xl left-0 right-0 mx-auto md:left-auto md:right-5 md:mx-0"
            style={{
              width: "min(92vw, 360px)",
              height: "min(70vh, 480px)",
              background: "color-mix(in srgb, var(--bg) 78%, transparent)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              border: "1px solid color-mix(in srgb, var(--brand-cyan) 28%, transparent)",
              boxShadow:
                "0 12px 40px rgba(0,0,0,0.25), 0 0 24px color-mix(in srgb, var(--brand-cyan) 14%, transparent)",
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3 shrink-0"
              style={{ borderBottom: "1px solid var(--border)" }}
            >
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span
                    className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping"
                    style={{ background: status === "connected" ? "#2ecc71" : "#f59e0b" }}
                  />
                  <span
                    className="relative inline-flex rounded-full h-2.5 w-2.5"
                    style={{ background: status === "connected" ? "#2ecc71" : "#f59e0b" }}
                  />
                </span>
                <span className="text-[13.5px] font-semibold" style={{ color: "var(--fg)" }}>
                  Asistanla Konuş
                </span>
              </div>
              <button
                onClick={() => { setPanelOpen(false); window.VerasistChatWidget?.end(); }}
                aria-label="Kapat"
                className="p-1.5 rounded-full transition-colors hover:bg-white/5"
                style={{ color: "var(--muted)" }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Messages */}
            <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 flex flex-col gap-3" role="log" aria-live="polite">
              {messages.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-center">
                  <p className="text-[13px]" style={{ color: "var(--muted)" }}>
                    {status === "connecting" ? "Asistan bağlanıyor…" : "Bir şeyler sorabilirsiniz."}
                  </p>
                </div>
              ) : (
                messages.map((m, i) => (
                  <div
                    key={m.turnId ? `${m.turnId}-${m.role}` : i}
                    className="flex"
                    style={{ justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}
                  >
                    <div
                      className="max-w-[80%] px-3.5 py-2.5 text-[13.5px] leading-relaxed break-words whitespace-pre-wrap min-w-0"
                      style={
                        m.role === "user"
                          ? {
                              background: "color-mix(in srgb, var(--brand-cyan) 8%, transparent)",
                              color: "var(--fg)",
                              border: "1px solid var(--brand-cyan)",
                              borderRadius: "16px 16px 6px 16px",
                              backdropFilter: "blur(8px)",
                              WebkitBackdropFilter: "blur(8px)",
                              boxShadow:
                                "0 0 12px color-mix(in srgb, var(--brand-cyan) 20%, transparent)",
                            }
                          : {
                              background: "color-mix(in srgb, #2ecc71 8%, transparent)",
                              color: "var(--fg)",
                              border: "1px solid #2ecc71",
                              borderRadius: "16px 16px 16px 6px",
                              backdropFilter: "blur(8px)",
                              WebkitBackdropFilter: "blur(8px)",
                              boxShadow:
                                "0 0 12px color-mix(in srgb, #2ecc71 20%, transparent)",
                            }
                      }
                    >
                      {m.content}
                    </div>
                  </div>
                ))
              )}
            {tools.length > 0 && <div role="status" aria-label="Araç çağrıları" className="px-4 py-2 text-xs text-blue-300 break-words">{tools.map(tool => <div key={`${tool.turn_id}:${tool.tool_call_id}`}>
                {tool.status === "running" ? "◌" : tool.status === "failed" ? "!" : "✓"} {tool.function_name.replace(/_/g, " ")} · {tool.status === "running" ? (busy ? "Çalışıyor…" : "Durum doğrulanamadı") : tool.status === "failed" ? "Başarısız" : "Tamamlandı"}
              </div>)}</div>}
            </div>

              {busy && <p className="px-4 pb-2 text-xs" role="status" style={{ color: "var(--muted)" }}>Asistan yanıtlıyor…</p>}
            {chatError && <p className="px-4 pb-2 text-xs text-red-500" role="alert">{chatError}</p>}
            {/* Input */}
            <div
              className="flex items-center gap-2 px-3 py-3 shrink-0"
              style={{ borderTop: "1px solid var(--border)" }}
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.nativeEvent.isComposing) handleSend();
                }}
                placeholder="Mesajınızı yazın…"
                disabled={status !== "connected" || busy}
                className="chat-widget-input flex-1 min-w-0 rounded-full px-4 py-2.5 text-[13.5px] outline-none"
                style={{
                  background: "transparent",
                  border: "1px solid color-mix(in srgb, var(--brand-cyan) 45%, transparent)",
                  color: "var(--fg)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  boxShadow: "inset 0 0 8px color-mix(in srgb, var(--brand-cyan) 8%, transparent)",
                  opacity: status === "connected" ? 1 : 0.55,
                }}
              />
              <button
                onClick={handleSend}
                disabled={status !== "connected" || busy || !input.trim()}
                aria-label="Gönder"
                className="chat-widget-send w-10 h-10 shrink-0 rounded-full flex items-center justify-center"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <LeadCaptureModal
        open={leadModalOpen}
        onClose={() => setLeadModalOpen(false)}
        onSubmit={handleLeadSubmit}
        submitting={leadSubmitting}
        error={leadError}
      />
    </>
  );
};

export default ChatWidgetButton;
